import { request } from '@umijs/max';
import type { Project, ProjectListParams } from './projects';

/**
 * 过滤并序列化查询参数
 *
 * @param params - 原始查询对象
 */
const buildProjectParams = (
  params?: ProjectListParams,
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const result: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        result[key] = value.join(',');
      }
      return;
    }

    result[key] = value as string | number;
  });

  return Object.keys(result).length ? result : undefined;
};

/**
 * 项目收藏夹
 */
export interface ProjectCollection {
  /** 收藏夹 UUID */
  uuid: string;
  /** 收藏夹名称 */
  name: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；缺失或为空表示根收藏夹 */
  parentUuid?: string | null;
  /** 子收藏夹（前端组装树时使用） */
  children?: ProjectCollection[];
  /** 创建者 UUID */
  createdByUuid: string;
  /** 项目数量 */
  projectCount?: number;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
}

/**
 * 创建收藏夹参数
 */
export interface CreateProjectCollectionDto {
  /** 收藏夹名称 */
  name: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；缺失或为空表示根收藏夹 */
  parentUuid?: string | null;
}

/**
 * 更新收藏夹参数
 */
export interface UpdateProjectCollectionDto {
  /** 收藏夹名称 */
  name?: string;
  /** 收藏夹描述 */
  description?: string;
  /** 不传时保留原上级；传 null 表示移动到根收藏夹 */
  parentUuid?: string | null;
}

/**
 * 收藏夹项目列表响应
 */
export interface CollectionProjectsResponse {
  /** 数据列表 */
  data: Project[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 单页数量 */
  limit: number;
}

/**
 * 批量添加项目到收藏夹响应
 */
export interface BatchAddProjectsResponse {
  /** 是否全部成功 */
  success: boolean;
  /** 提示信息 */
  message: string;
  /** 成功数量 */
  successCount: number;
  /** 失败数量 */
  failCount: number;
  /** 失败列表 */
  failList?: Array<{ projectUuid: string; reason: string }>;
}

/**
 * 项目收藏夹 API
 */
export const projectCollectionApi = {
  /**
   * 获取所有收藏夹列表
   */
  getCollections: () => {
    return request<ProjectCollection[]>(
      '/bff/luole-hunter/project-collections',
    );
  },

  /**
   * 获取收藏夹详情
   *
   * @param uuid - 收藏夹 UUID
   */
  getCollection: (uuid: string) => {
    return request<ProjectCollection>(
      `/bff/luole-hunter/project-collections/${uuid}`,
    );
  },

  /**
   * 创建收藏夹
   *
   * @param data - 创建收藏夹数据
   */
  createCollection: (data: CreateProjectCollectionDto) => {
    return request<ProjectCollection>('/bff/luole-hunter/project-collections', {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新收藏夹
   *
   * @param uuid - 收藏夹 UUID
   * @param data - 更新收藏夹数据
   */
  updateCollection: (uuid: string, data: UpdateProjectCollectionDto) => {
    return request<ProjectCollection>(
      `/bff/luole-hunter/project-collections/${uuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除收藏夹
   *
   * @param uuid - 收藏夹 UUID
   */
  deleteCollection: (uuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/project-collections/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 获取收藏夹中的项目列表
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param params - 查询参数
   */
  getCollectionProjects: (
    collectionUuid: string,
    params?: ProjectListParams,
  ) => {
    const queryParams = buildProjectParams(params);
    const options = queryParams ? { params: queryParams } : {};
    return request<CollectionProjectsResponse>(
      `/bff/luole-hunter/project-collections/${collectionUuid}/projects`,
      options,
    );
  },

  /**
   * 添加项目到收藏夹
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param projectUuid - 项目 UUID
   */
  addProjectToCollection: (collectionUuid: string, projectUuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/project-collections/${collectionUuid}/projects`,
      {
        method: 'POST',
        data: { projectUuid },
      },
    );
  },

  /**
   * 从收藏夹移除项目
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param projectUuid - 项目 UUID
   */
  removeProjectFromCollection: (
    collectionUuid: string,
    projectUuid: string,
  ) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/project-collections/${collectionUuid}/projects/${projectUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 批量添加项目到收藏夹
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param projectUuids - 项目 UUID 列表
   */
  batchAddProjectsToCollection: (
    collectionUuid: string,
    projectUuids: string[],
  ) => {
    return request<BatchAddProjectsResponse>(
      `/bff/luole-hunter/project-collections/${collectionUuid}/projects/batch`,
      {
        method: 'POST',
        data: { projectUuids },
      },
    );
  },
};
